import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { seoArticles } from '@/mocks/articles';

export default function PoradnikZakupowyPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        {/* Hero */}
        <section className="relative bg-background-100 py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-accent-100/60 via-background-50 to-secondary-100/40"></div>
          <div className="relative w-full max-w-4xl mx-auto px-6 md:px-10">
            <div className="flex items-center gap-3 mb-6">
              <span className="px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold tracking-wider uppercase">Poradnik zakupowy</span>
              <span className="text-xs text-foreground-400">16 lipca 2026</span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 font-heading leading-tight mb-5">
              Poradnik zakupowy Kakobuy –<br />
              <span className="text-accent-600">Jak kupować mądrze i bezpiecznie?</span>
            </h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl leading-relaxed mb-8">
              Kompletny przewodnik po zakupach z Kakobuy. Dowiedz się jak znaleźć najlepsze produkty,
              bezpiecznie dokonać zamówienia i zaoszczędzić pieniądze. Wszystko w jednym miejscu.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-bag-3-line"></i>
                Kakobuy Spreadsheet
              </a>
              <a
                href="#kategorie"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 border border-background-200/60 text-foreground-700 font-semibold text-sm hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-grid-line"></i>
                Przeglądaj kategorie
              </a>
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-6 md:px-10">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading text-center mb-12">
              Jak zacząć zakupy? Prosty plan w 5 krokach
            </h2>
            <div className="relative pl-8 border-l-2 border-accent-200 space-y-10">
              {[
                { title: 'Wybierz kategorię', desc: 'Na stronie głównej znajdziesz rozwijane menu z 14 kategoriami produktów. Wybierz tę, która Cię interesuje – od butów i ubrań po akcesoria i perfumy.', icon: 'ri-list-check-3' },
                { title: 'Przejrzyj produkty w arkuszu', desc: 'Kakobuy Spreadsheet zawiera uporządkowaną listę produktów z cenami, linkami i opiniami społeczności. Wszystko w jednym miejscu, bez konieczności przeszukiwania setek stron.', icon: 'ri-file-list-3-line' },
                { title: 'Zweryfikuj sprzedawcę', desc: 'Sprawdź oceny, datę rejestracji i opinie innych kupujących. Dobre przygotowanie na tym etapie pozwala uniknąć wielu problemów.', icon: 'ri-shield-check-line' },
                { title: 'Zamów QC zdjęcia', desc: 'Poproś o zdjęcia kontrolne produktu przed wysyłką międzynarodową. To kluczowy moment – możesz zweryfikować jakość i zgodność z opisem.', icon: 'ri-camera-line' },
                { title: 'Sfinalizuj zamówienie', desc: 'Po pozytywnej weryfikacji QC, zatwierdź wysyłkę i śledź status paczki. Pamiętaj o potencjalnych opłatach celnych przy imporcie do Polski.', icon: 'ri-send-plane-line' },
              ].map((step, i) => (
                <div key={i} className="relative">
                  <div className="absolute -left-[2.15rem] top-1 w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center">
                    <span className="text-background-50 text-xs font-bold">{i + 1}</span>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <i className={`${step.icon} text-accent-600 text-lg`}></i>
                    <h3 className="text-lg font-bold text-foreground-900 font-heading">{step.title}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Grid */}
        <section id="kategorie" className="py-16 md:py-24 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading text-center mb-4">
              Wszystkie kategorie produktów
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-12 max-w-xl mx-auto">
              Wybierz interesującą Cię kategorię, aby przejść do szczegółowej listy produktów w głównym serwisie Kakobuy Spreadsheet.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden cursor-pointer transition-all duration-300 hover:border-accent-300 hover:shadow-md"
                >
                  <div className="aspect-square relative overflow-hidden bg-background-200/30">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <i className="ri-shopping-bag-line text-foreground-300 text-3xl group-hover:text-accent-400 transition-colors duration-300"></i>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-sm font-semibold text-background-50 font-heading whitespace-nowrap">{cat.name}</p>
                      <p className="text-xs text-background-50/70 whitespace-nowrap">{cat.nameEn}</p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Tips */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-6 md:px-10">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading text-center mb-12">
              Złote zasady bezpiecznych zakupów
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: 'Zawsze sprawdzaj sprzedawcę', desc: 'Data rejestracji, liczba transakcji i oceny to podstawowe wskaźniki wiarygodności. Nie sugeruj się wyłącznie niską ceną.', icon: 'ri-user-search-line' },
                { title: 'Korzystaj z QC zdjęć', desc: 'Zdjęcia kontrolne to Twoje prawo jako kupującego. Zawsze o nie proś i dokładnie analizuj przed zatwierdzeniem wysyłki.', icon: 'ri-image-edit-line' },
                { title: 'Porównuj ceny', desc: 'Ten sam produkt może być oferowany przez różnych sprzedawców w różnych cenach. Poświęć chwilę na porównanie ofert.', icon: 'ri-scales-3-line' },
                { title: 'Czytaj opinie społeczności', desc: 'Doświadczenia innych kupujących są bezcenne. Korzystaj z recenzji i rekomendacji dostępnych w arkuszu.', icon: 'ri-chat-quote-line' },
                { title: 'Kalkuluj całkowity koszt', desc: 'Cena produktu to nie wszystko. Dolicz wysyłkę, potencjalne cło i VAT, aby poznać rzeczywisty koszt zamówienia.', icon: 'ri-calculator-line' },
                { title: 'Chroń swoje dane', desc: 'Nigdy nie udostępniaj poufnych informacji poza oficjalnym systemem płatności. Korzystaj z bezpiecznych metod płatności.', icon: 'ri-lock-password-line' },
              ].map((tip, i) => (
                <div key={i} className="flex gap-4 p-5 rounded-xl bg-background-100 border border-background-200/60 transition-all duration-200 hover:border-accent-200">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${tip.icon} text-accent-600 text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-foreground-900 font-heading mb-1.5">{tip.title}</h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">{tip.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading text-center mb-4">
              Więcej poradników
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-12 max-w-xl mx-auto">
              Rozszerz swoją wiedzę o zakupach z Kakobuy. Przeczytaj nasze szczegółowe artykuły.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {seoArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/artykul/${article.slug}`}
                  className="group bg-background-50 rounded-2xl border border-background-200/60 overflow-hidden cursor-pointer transition-all duration-300 hover:border-accent-300 hover:shadow-md"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <span className="inline-block px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold tracking-wider uppercase mb-3">
                      {article.category}
                    </span>
                    <h3 className="text-base font-bold text-foreground-900 font-heading mb-2 group-hover:text-accent-700 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="whitespace-nowrap">{article.date}</span>
                      <span>·</span>
                      <span className="whitespace-nowrap">{article.readTime} min czytania</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-primary-500">
          <div className="w-full max-w-3xl mx-auto px-6 md:px-10 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-background-50 font-heading mb-4">
              Gotowy na pierwsze zakupy?
            </h2>
            <p className="text-sm md:text-base text-background-50/80 mb-8 max-w-xl mx-auto leading-relaxed">
              Przejdź do głównego serwisu Kakobuy Spreadsheet i odkryj setki sprawdzonych produktów w najlepszych cenach.
              Twoje wymarzone znaleziska czekają.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-background-50 text-primary-600 font-semibold text-base hover:bg-background-100 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg"
              >
                <i className="ri-shopping-bag-3-line"></i>
                Kakobuy Spreadsheet
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-600 text-background-50 font-semibold text-base border border-background-50/30 hover:bg-primary-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-4-line"></i>
                Strona główna
              </Link>
            </div>
          </div>
        </section>

        {/* Schema */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Poradnik zakupowy Kakobuy – Jak kupować mądrze i bezpiecznie?",
          "description": "Kompletny przewodnik po zakupach z Kakobuy. Dowiedz się jak znaleźć najlepsze produkty, bezpiecznie dokonać zamówienia i zaoszczędzić pieniądze.",
          "datePublished": "2026-07-16",
          "dateModified": "2026-07-16",
          "author": { "@type": "Organization", "name": "KakobuyFinds" },
          "inLanguage": "pl-PL",
          "mainEntityOfPage": { "@type": "WebPage", "@id": "https://kakobuyfinds.autos/poradnik-zakupowy" }
        }) }}></script>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Strona główna", "item": "https://kakobuyfinds.autos/" },
            { "@type": "ListItem", "position": 2, "name": "Poradnik zakupowy", "item": "https://kakobuyfinds.autos/poradnik-zakupowy" }
          ]
        }) }}></script>
      </main>
      <Footer />
    </>
  );
}